import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { translate } from '../../../../utils/Common';
import styles from './StatusFilterPanel.module.css';

const StatusFilterPanel = ({ statusOptions = [], value, onChange, onClose }) => {
  const panelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose?.();
      }
    };

    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  // Convertir value a array de números para multi-selección
  const getSelectedValues = () => {
    if (!value || value === 'all' || value === '') return [];
    if (typeof value === 'string') {
      return value.split(',').map(v => v.trim()).filter(v => v).map(v => parseInt(v, 10)).filter(n => !isNaN(n));
    }
    if (Array.isArray(value)) {
      return value.map(v => parseInt(v, 10)).filter(n => !isNaN(n));
    }
    return [parseInt(value, 10)].filter(n => !isNaN(n));
  };

  const selectedValues = getSelectedValues();

  const handleSelect = (statusValue) => {
    const statusNum = parseInt(statusValue, 10);
    if (isNaN(statusNum)) return;

    const currentSelected = [...selectedValues];
    const index = currentSelected.indexOf(statusNum);

    if (index > -1) {
      // Si ya está seleccionado, quitarlo
      currentSelected.splice(index, 1);
    } else {
      // Si no está seleccionado, agregarlo
      currentSelected.push(statusNum);
    }

    // Convertir array a string separado por comas
    if (currentSelected.length === 0) {
      onChange?.('');
    } else {
      onChange?.(currentSelected.sort((a, b) => a - b).join(','));
    }
  };

  const handleClear = () => {
    onChange?.('');
  };

  return (
    <div className={styles.backdrop}>
      <div className={styles.panel} ref={panelRef}>
        <div className={styles.header}>
          <span className={styles.title}>{translate('dashboard.packages.search.moreStatus') || 'More status'}</span>
          <button type="button" className={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className={styles.body}>
          {statusOptions.map((status) => {
            const statusNum = parseInt(status.value, 10);
            const isActive = selectedValues.includes(statusNum);
            return (
              <button
                key={status.value}
                type="button"
                className={`${styles.statusItem} ${isActive ? styles.active : ''}`}
                onClick={() => handleSelect(status.value)}
              >
                {status.icon && <span className={styles.icon}>{status.icon}</span>}
                <span className={styles.label}>{status.label}</span>
                {isActive && <span className={styles.check}>✓</span>}
              </button>
            );
          })}
        </div>

        <div className={styles.panelFooter}>
          <button type="button" className={styles.clearButton} onClick={handleClear}>
            {translate('dashboard.packages.search.clearStatus') || 'Clear status'}
          </button>
          <button type="button" className={styles.doneButton} onClick={onClose}>
            {translate('global.done') || translate('global.accept') || 'Done'}
          </button>
        </div>
      </div>
    </div>
  );
};

StatusFilterPanel.propTypes = {
  statusOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
    })
  ),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
  onChange: PropTypes.func,
  onClose: PropTypes.func,
};

export default StatusFilterPanel;

